"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { routes } from "@/lib/routes";
import { Leaf, Building2, ArrowRight, ArrowDown, Check, ChevronDown, TreePine } from "lucide-react";

// Product options for dropdowns
interface ProductOption {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const demandProducts: ProductOption[] = [
  { id: "oekopunkte", label: "Ökopunkte", icon: <Leaf className="w-5 h-5" /> },
  {
    id: "ausgleichsflaechen",
    label: "Ausgleichsflächen",
    icon: <TreePine className="w-5 h-5" />,
  },
];

// CTA labels for demand side
const demandCtaLabels: Record<string, string> = {
  oekopunkte: "Ökopunkte kaufen",
  ausgleichsflaechen: "Ausgleichsflächen finden",
};

// Inline dropdown component for titles
interface InlineDropdownProps {
  selected: ProductOption;
  onSelect: (product: ProductOption) => void;
  options: ProductOption[];
  variant: "light" | "dark";
}

function InlineDropdown({ selected, onSelect, options, variant }: InlineDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isLight = variant === "light";

  return (
    <span ref={dropdownRef} className={`relative inline-block ${isOpen ? "z-50" : ""}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1 border-b-2 border-dashed transition-all duration-200
          ${
            isLight
              ? "text-accent border-accent hover:border-solid"
              : "text-accent border-accent hover:border-solid"
          }`}
      >
        <span>{selected.label}</span>
        <ChevronDown
          className={`w-5 h-5 md:w-6 md:h-6 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 z-50 mt-2 min-w-[200px] rounded-xl shadow-xl border overflow-hidden
          animate-in fade-in slide-in-from-top-2 duration-200
          ${isLight ? "bg-white border-zinc-200" : "bg-white border-zinc-200"}`}
        >
          <div className="py-2">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-base font-normal
                  ${
                    selected.id === option.id
                      ? "bg-primary/5 text-primary"
                      : "text-zinc-700 hover:bg-zinc-50"
                  }`}
              >
                <span className={selected.id === option.id ? "text-accent" : "text-zinc-400"}>
                  {option.icon}
                </span>
                <span className="font-medium">{option.label}</span>
                {selected.id === option.id && <Check className="w-4 h-4 ml-auto text-accent" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}

// Supply Card (Left - Dark)
function SupplyCard() {
  const benefits = [
    "Deutlich höhere Erträge als bei klassischer Verpachtung",
    "Zusätzliche passive Einkommensquelle",
    "Kostenfreie und unverbindliche Potenzialanalyse",
    "Rechtssichere Verträge und professionelle Betreuung",
  ];

  return (
    <div className="relative flex flex-col h-full p-8 md:p-10 lg:p-12 transition-all duration-300 bg-gradient-to-br from-primary to-primary/80 animate-in fade-in slide-in-from-left-4 duration-500">
      {/* Icon Badge */}
      <div className="relative z-10 w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-4 md:mb-6 bg-white/10 text-white">
        <Leaf className="w-7 h-7 md:w-8 md:h-8" />
      </div>

      {/* Subtitle — persona cue mirroring the demand card's "Vorhabenträger" */}
      <p className="relative z-10 text-sm font-medium uppercase tracking-wider mb-2 text-accent">
        Flächeneigentümer &amp; Landwirte
      </p>

      {/* Title - min-h ensures alignment with demand side */}
      <h2 className="relative z-10 text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-4 text-white md:min-h-[3.5rem] lg:min-h-[4rem]">
        Landeigentümer
      </h2>

      {/* Description - min-h ensures alignment */}
      <p className="relative z-10 text-base md:text-lg mb-6 text-white/80 md:min-h-[4.5rem]">
        Generieren Sie mit Ihrem Land zusätzliche Erträge – ohne eigenen Aufwand, professionell
        betreut.
      </p>

      {/* Benefits */}
      <ul className="relative z-10 space-y-4 mb-8">
        {benefits.map((benefit, index) => (
          <li
            key={index}
            className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2"
            style={{
              animationDelay: `${300 + index * 100}ms`,
              animationFillMode: "both",
            }}
          >
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-accent" />
            <span className="text-white/90">{benefit}</span>
          </li>
        ))}
      </ul>

      {/* CTAs */}
      <div className="relative z-10 mt-auto">
        <p className="text-white/80 mb-3">Ich will verkaufen</p>
        <div className="flex flex-col gap-3">
          <Link
            href={routes.schnellangebot}
            className="group flex items-center justify-between px-5 py-3 rounded-full bg-white text-primary hover:bg-primary hover:text-white font-semibold transition-all duration-200"
          >
            <span>Kompensation verkaufen</span>
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            href={routes.flaechenPruefen}
            className="group flex items-center justify-between px-5 py-3 rounded-full bg-white/15 text-white hover:bg-white/25 font-semibold transition-all duration-200"
          >
            <span>Flächen prüfen lassen</span>
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// Demand Card (Right - Light)
function DemandCard() {
  const [selectedProduct, setSelectedProduct] = useState<ProductOption>(demandProducts[0]);

  const benefits = [
    "Sofortzugriff auf Angebote in allen Naturräumen",
    "Behördlich geprüfte und anerkannte Maßnahmen",
    "Ein Ansprechpartner von Anfrage bis Nachweis",
    "Transparente Festpreise ohne versteckte Kosten",
  ];

  return (
    <div
      className="relative flex flex-col h-full p-8 md:p-10 lg:p-12 transition-all duration-300 bg-gradient-to-br from-white to-zinc-50 md:shadow-[-20px_0_40px_-20px_rgba(0,0,0,0.1)] animate-in fade-in slide-in-from-right-4 duration-500 delay-150"
      style={{ animationFillMode: "both" }}
    >
      {/* Icon Badge */}
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-4 md:mb-6 bg-primary/10 text-primary">
        <Building2 className="w-7 h-7 md:w-8 md:h-8" />
      </div>

      {/* Subtitle */}
      <p className="text-sm font-medium uppercase tracking-wider mb-2 text-accent">
        Vorhabenträger
      </p>

      {/* Title with inline dropdown - min-h ensures alignment */}
      <h2 className="relative z-20 text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-4 text-primary md:min-h-[3.5rem] lg:min-h-[4rem]">
        Ich will{" "}
        <InlineDropdown
          selected={selectedProduct}
          onSelect={setSelectedProduct}
          options={demandProducts}
          variant="light"
        />{" "}
        kaufen
      </h2>

      {/* Description - min-h ensures alignment */}
      <p className="text-base md:text-lg mb-6 text-zinc-600 md:min-h-[4.5rem]">
        Zügig, rechtssicher, transparent – für eine sichere Genehmigung Ihres Bauvorhabens.
      </p>

      {/* Benefits */}
      <ul className="space-y-4 mb-8">
        {benefits.map((benefit, index) => (
          <li
            key={index}
            className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2"
            style={{
              animationDelay: `${450 + index * 100}ms`,
              animationFillMode: "both",
            }}
          >
            <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-accent" />
            <span className="text-zinc-700">{benefit}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-auto flex flex-col gap-3">
        <Link
          href={routes.schnellanfrage}
          className="group flex items-center justify-between px-5 py-3 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium transition-all duration-200"
        >
          {demandCtaLabels[selectedProduct.id]}
          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <div className="min-h-[48px] flex items-center pl-5">
          <Link
            href={routes.content.kompensationsberatung}
            className="text-sm text-zinc-500 hover:text-primary transition-colors"
          >
            Regelmäßiger Bedarf?{" "}
            <span className="underline underline-offset-2">Managed Service</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export function RoleSelectorHero() {
  const scrollToNextSection = () => {
    const element = document.getElementById("plattform");
    if (element) {
      const headerHeight = 64;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  // Calculate height: 100svh minus header (64px/4rem)
  return (
    <section className="relative min-h-[calc(100svh-4rem)] flex flex-col">
      {/* Two-Column Split */}
      <div className="grid md:grid-cols-2 flex-1">
        {/* Supply Side - Land Owners */}
        <SupplyCard />

        {/* Demand Side - Buyers */}
        <DemandCard />
      </div>

      {/* Scroll indicator at bottom */}
      <div className="hidden md:block absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20">
        <button
          onClick={scrollToNextSection}
          className="p-2.5 rounded-full bg-white shadow-lg border-2 border-zinc-200 text-primary transition-all duration-300 cursor-pointer"
        >
          <span className="sr-only">Mehr erfahren</span>
          <ArrowDown
            className="w-[18px] h-[18px]"
            style={{
              animation: "bounce-down 1s infinite",
            }}
          />
        </button>
      </div>
    </section>
  );
}
